const { BigInt } = window;

export function formatUnits(value) {
  if (value == null) return '—';
  const units = BigInt(value);
  const absolute = units < 0n ? -units : units;
  return `${units < 0n ? '−' : ''}${(absolute / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${String(absolute % 100n).padStart(2, '0')}`;
}

export function formatOrderDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' }) : '—';
}

export function summarizeExport(rows) {
  const currencies = new Map();
  const statuses = Object.create(null);
  for (const row of rows) {
    const status = row.status || 'UNKNOWN';
    statuses[status] = (statuses[status] || 0) + 1;
    if (!currencies.has(row.currency)) {
      currencies.set(row.currency, {
        currency: row.currency, count: 0, paidCount: 0, invalidAmounts: 0,
        totalUnits: 0n, amountUnits: 0n, randomUnits: 0n, paidTotalUnits: 0n, paidAmountUnits: 0n,
      });
    }
    const item = currencies.get(row.currency);
    item.count += 1;
    if (row.totalUnits == null || row.amountUnits == null) item.invalidAmounts += 1;
    item.totalUnits += BigInt(row.totalUnits || 0);
    item.amountUnits += BigInt(row.amountUnits || 0);
    item.randomUnits += BigInt(row.randomUnits || 0);
    if (status === 'PAID') {
      item.paidCount += 1;
      item.paidTotalUnits += BigInt(row.totalUnits || 0);
      item.paidAmountUnits += BigInt(row.amountUnits || 0);
    }
  }
  return {
    count: rows.length, paidCount: statuses.PAID || 0, statuses,
    currencies: [...currencies.values()].sort((a, b) => a.currency.localeCompare(b.currency)).map(item =>
      Object.fromEntries(Object.entries(item).map(([key, value]) => [key, typeof value === 'bigint' ? String(value) : value]))),
  };
}

function moneyCell(value) {
  if (value == null) return '';
  const units = BigInt(value);
  const abs = units < 0n ? -units : units;
  // Excel keeps at most 15 significant digits. Keep larger values as text.
  if (abs.toString().length > 15) {
    return `${units < 0n ? '-' : ''}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}`;
  }
  return { t: 'n', v: Number(units) / 100, z: '#,##0.00' };
}

export function buildOrdersWorkbook(XLSX, { rows, summary, range, generatedAt, filterLabels }) {
  const workbook = XLSX.utils.book_new();
  const headers = [
    '№', 'ID ордера', 'ID платежа', 'Магазин', 'Базовая сумма', 'К оплате',
    'Разница', 'Добавочная сумма', 'Валюта', 'Статус', 'Тип', 'Платформа',
    'Способ оплаты', 'Telegram ID', 'Создан (МСК)', 'Оплачен (МСК)', 'Внутренний ID',
  ];
  const chunkSize = 50000;
  for (let start = 0; start < Math.max(rows.length, 1); start += chunkSize) {
    const values = rows.slice(start, start + chunkSize).map((row, index) => [
      start + index + 1, String(row.orderId || ''), String(row.paymentId || ''), String(row.merchant || ''),
      moneyCell(row.amountUnits), moneyCell(row.totalUnits),
      row.totalUnits == null || row.amountUnits == null ? '' : moneyCell(BigInt(row.totalUnits) - BigInt(row.amountUnits)),
      moneyCell(row.randomUnits), row.currency, row.status, row.type, row.platform,
      row.paymentMethod || '', String(row.tgId || ''), formatOrderDate(row.createdAt),
      formatOrderDate(row.paidAt), String(row._id),
    ]);
    const sheet = XLSX.utils.aoa_to_sheet([headers, ...values]);
    sheet['!cols'] = [6, 38, 30, 25, 20, 20, 20, 20, 12, 22, 16, 22, 20, 22, 24, 24, 28].map(wch => ({ wch }));
    sheet['!autofilter'] = { ref: sheet['!ref'] };
    XLSX.utils.book_append_sheet(workbook, sheet, start ? `Ордера ${start / chunkSize + 1}` : 'Ордера');
  }
  const totals = [
    ['Выгрузка обычных ордеров'],
    ['Сформирована (МСК)', formatOrderDate(generatedAt)],
    ['От ордера', String(range.from.orderId)],
    ['По ордер', String(range.to.orderId)],
    ['Начало (МСК)', formatOrderDate(range.from.createdAt)],
    ['Конец (МСК)', formatOrderDate(range.to.createdAt)],
    ['Платформа', filterLabels?.platform || range.filters?.platform || 'Все платформы'],
    ['Мерчанты', filterLabels?.merchants || range.filters?.merchantIds?.join(', ') || 'Все мерчанты'],
    ['Выбранные статусы', filterLabels?.statuses || range.filters?.statuses?.join(', ') || 'Все статусы'],
    ['Всего ордеров', summary.count], ['Оплачено', summary.paidCount], [],
    ['Валюта', 'Ордеров', 'Оплачено', 'К оплате, выборка', 'Базовая сумма, выборка', 'Добавочная сумма',
      'К оплате, PAID', 'Базовая сумма, PAID', 'Разница, PAID', 'Некорректные суммы'],
    ...summary.currencies.map(item => [
      item.currency, item.count, item.paidCount, moneyCell(item.totalUnits), moneyCell(item.amountUnits),
      moneyCell(item.randomUnits), moneyCell(item.paidTotalUnits), moneyCell(item.paidAmountUnits),
      moneyCell(BigInt(item.paidTotalUnits) - BigInt(item.paidAmountUnits)), item.invalidAmounts,
    ]),
    [], ['Статус', 'Количество'],
    ...Object.entries(summary.statuses).sort().map(([status, count]) => [status, count]),
    [], ['Базовая сумма = amount; к оплате = total_amount. Добавочная сумма = rand_summ, отдельно.'],
    ['Разница не является чистой прибылью: комиссии и прочие расходы не вычтены.'],
    ['Границы включительно; платформа, мерчанты и статусы выбраны в окне выгрузки.'],
    ['Итоги рассчитаны по строкам файла. Данные читаются во время формирования выгрузки.'],
    ['Отсутствующие или некорректные суммы пусты в таблице и не включены в соответствующие итоги.'],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(totals);
  summarySheet['!cols'] = [35, 42, 22, 24, 25, 25, 24, 25, 25, 25].map(wch => ({ wch }));
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Итоги');
  return workbook;
}

export async function saveOrdersWorkbook(data, { signal } = {}) {
  const XLSX = await import('xlsx');
  if (signal?.aborted) return;
  const workbook = buildOrdersWorkbook(XLSX, data);
  if (signal?.aborted) return;
  XLSX.writeFile(workbook, `orders_${new Date(data.generatedAt).toISOString().replace(/[:.]/g, '-')}.xlsx`, { compression: true });
}
